import type { InstrumentDatePlan, InstrumentDateReference } from './datePlan'
import type { BookFieldDefinition, BookEditorSchema } from '../rkdecks/bookReview'

export type ObjectKind = 'entity' | 'instrument' | 'position' | 'trade'
export interface EditScope {
  fund_id?: string | null
  client_id?: string | null
  holder_id?: string | null
  instrument_id?: string | null
  issuer_id?: string | null
}
export interface EditTarget {
  kind: ObjectKind
  id: string
  label: string
}
export interface Preview {
  date_references?: InstrumentDateReference[]
  date_plan?: InstrumentDatePlan | null
  preview_token: string
  target_type: ObjectKind
  target_id: string
  scope: EditScope
  values: Record<string, unknown>
  sources: Record<
    string,
    { record_event_id?: string | null; checkpoint_id?: string | null; revision?: string | null }
  >
  expires_at: string
}
export interface EditField {
  application_scope?: 'selected_reference'
  selected_reference_id?: string
  resulting_current_value?: string | null
  id: string
  field: string
  approved_value: unknown
  baseline_value: unknown
  proposed_value: unknown
  record_available: boolean
  status: string
  materiality: string
  binding_stale: boolean
}
export interface EditResult {
  date_plan?: InstrumentDatePlan | null
  id: string
  idempotency_key: string
  target_type: ObjectKind
  target_id: string
  scope: EditScope
  status: string
  binding: string
  submitted_by: string
  explanation?: string | null
  fields: EditField[]
  action_kind: string
  required_fields: string[]
  current_record_available: boolean
  current_record_unavailable_reason?: string | null
  warning_token?: string | null
  message?: string | null
}
export interface UnchangedResult {
  status: 'unchanged'
  target_type: ObjectKind
  target_id: string
  scope: EditScope
}
export interface Lifecycle {
  kind: 'amend' | 'undo'
  edit: EditResult
  fieldIds: string[]
}
export type Definition = BookFieldDefinition
export type Catalogue = { fields: Definition[] }

export function scopeKey(scope: EditScope): string {
  return JSON.stringify(
    ['fund_id', 'client_id', 'holder_id', 'instrument_id', 'issuer_id'].map(
      (key) => scope[key as keyof EditScope] ?? null
    )
  )
}
export function matchesTarget(
  value: { target_type: string; target_id: string; scope: EditScope },
  target: EditTarget,
  scope: EditScope
): boolean {
  return (
    value.target_type === target.kind &&
    value.target_id === target.id &&
    scopeKey(value.scope) === scopeKey(scope)
  )
}
export function valueText(value: unknown): string {
  return value == null ? '' : typeof value === 'object' ? JSON.stringify(value) : String(value)
}
export function fieldSchema(field: Definition): BookEditorSchema {
  const schema = (field.correction?.value_schema ?? {}) as BookEditorSchema
  const alternatives = schema.anyOf ?? schema.oneOf
  return alternatives?.find((item) => item.type !== 'null') ?? schema
}
export function inputKind(field: Definition): 'date' | 'number' | 'enum' | 'boolean' | 'text' {
  const schema = fieldSchema(field)
  if (schema.enum) return 'enum'
  if (schema.type === 'boolean') return 'boolean'
  if (schema.format === 'date' || field.format === 'date') return 'date'
  if (
    ['number', 'integer'].includes(schema.type ?? '') ||
    ['number', 'quantity', 'money'].includes(field.format)
  )
    return 'number'
  return 'text'
}
export function parseValue(field: Definition, raw: string): unknown {
  if (!raw.trim()) {
    if (!field.correction?.nullable) throw new Error(`${field.label} is required.`)
    return null
  }
  if (inputKind(field) === 'number') {
    if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(raw.trim()))
      throw new Error(`${field.label} needs a number.`)
    return raw.trim() // Decimal values never pass through Number().
  }
  if (inputKind(field) === 'boolean') return raw === 'true'
  return raw
}
export function makePatch(
  fields: Definition[],
  draft: Record<string, string>,
  touched: ReadonlySet<string>,
  preview: Preview,
  required: readonly string[] = [],
  force: readonly string[] = []
): Record<string, unknown> {
  const patch: Record<string, unknown> = {}
  for (const field of fields) {
    if (!touched.has(field.key) && !required.includes(field.key) && !force.includes(field.key))
      continue
    const raw = draft[field.key] ?? ''
    if (
      raw !== valueText(preview.values[field.key]) ||
      required.includes(field.key) ||
      force.includes(field.key)
    )
      patch[field.key] = parseValue(field, raw)
  }
  return patch
}
export function resultLabel(status: string): string {
  return (
    (
      {
        applied: 'Recorded',
        pending: 'Pending manager approval',
        awaiting_ack: 'Awaiting your confirmation',
        rejected: 'Rejected',
        superseded: 'Replaced',
        cancelled: 'Cancelled',
        unchanged: 'No changes to record',
        partially_applied: 'Some changes recorded',
      } as Record<string, string>
    )[status] ?? status.replace(/_/g, ' ')
  )
}
